import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { type EmergencyCreditStatsCommon } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { CreditProgressBar } from "@src/appV2/redesign/EmergencyCredits/components/CreditProgressBar";
import { getCreditDisplayProps } from "@src/appV2/redesign/EmergencyCredits/utils/getCreditDisplayProps";
import { getCreditProgressBarLabel } from "@src/appV2/redesign/EmergencyCredits/utils/getCreditProgressBarLabel";
import {
  GOLDEN_RATIO_BOTTOM_FLEX,
  GOLDEN_RATIO_TOP_FLEX,
} from "@src/appV2/redesign/utils/goldenRatioLayout";
import { ScoreDisplay } from "@src/appV2/redesign/WorkerShifts/ShiftDetails/Timekeeping/EmergencyCreditsClockOutFlow/YourClipboardScore/components/ScoreDisplay";

import { RestrictedBadge } from "./components/RestrictedBadge";
import { PENALTY_SCORE_ANIMATION_DURATION_MS } from "./hooks/useCancellationScoreAnimation";

export interface ShiftCancelPageConfirmedData {
  finalScore: number;
  isWithinECWindow: boolean;
  finalECStats?: EmergencyCreditStatsCommon;
  isBecomingRestricted: boolean;
  isAlreadyRestricted: boolean;
  isFinalRestriction: boolean;
  reEnrollmentScore: number | undefined;
}

export interface ShiftCancelPageConfirmedHandlers {
  onContinue: () => void;
  isLoading?: boolean;
}

type ShiftCancelPageConfirmationScreenProps = Omit<
  ShiftCancelPageConfirmedData,
  "isFinalRestriction" | "reEnrollmentScore"
> &
  ShiftCancelPageConfirmedHandlers;

export function ShiftCancelPageConfirmationScreen(
  props: ShiftCancelPageConfirmationScreenProps
): JSX.Element {
  const {
    finalScore,
    isWithinECWindow,
    finalECStats,
    isBecomingRestricted,
    isAlreadyRestricted,
    isLoading = false,
    onContinue,
  } = props;

  const isRestricted = isBecomingRestricted || isAlreadyRestricted;
  const showScore = !isRestricted || isBecomingRestricted;

  const showCreditProgress =
    !isRestricted &&
    isWithinECWindow &&
    isDefined(finalECStats) &&
    finalECStats.totalAvailableCredits === 0;

  const creditDisplay = showCreditProgress ? getCreditDisplayProps(finalECStats) : undefined;

  return (
    <Stack textAlign="center" flex={1}>
      <Box sx={{ flex: GOLDEN_RATIO_TOP_FLEX, minHeight: 0 }} />

      <Stack spacing={12} sx={{ paddingX: 4 }}>
        <Stack alignItems="center" textAlign="center" spacing={6}>
          <Stack spacing={0} alignItems="center">
            <Text
              variant="h3"
              textAlign="center"
              sx={{
                color: (t) => (isRestricted ? t.palette.error.main : t.palette.intent?.error.text),
              }}
            >
              Shift cancelled
            </Text>
            {isBecomingRestricted ? (
              <Text
                variant="h3"
                textAlign="center"
                sx={{
                  color: (t) => t.palette.error.main,
                }}
              >
                Your account is Restricted
              </Text>
            ) : null}
          </Stack>
          {showScore && (
            <Stack sx={{ marginTop: "24px !important" }} alignItems="center">
              <Text
                variant="body2"
                sx={(t) => ({
                  color: t.palette.text.secondary,
                })}
              >
                Your new Clipboard Score
              </Text>
              <Box sx={{ marginTop: 8 }}>
                <ScoreDisplay
                  animationEnabled
                  startScore={finalScore}
                  currentEndScore={finalScore}
                  duration={PENALTY_SCORE_ANIMATION_DURATION_MS}
                />
              </Box>
            </Stack>
          )}
          {isRestricted ? (
            <Stack alignItems="center" sx={{ marginTop: "56px !important" }}>
              <RestrictedBadge />
            </Stack>
          ) : null}
          {showCreditProgress && creditDisplay && (
            <CreditProgressBar
              completedCount={creditDisplay.completedCount}
              requiredCount={creditDisplay.requiredCount}
              label={getCreditProgressBarLabel("cancellation", "unavailable")}
              creditType="cancellation"
              subtext={creditDisplay.subtext}
            />
          )}
        </Stack>
      </Stack>

      <Box sx={{ flex: GOLDEN_RATIO_BOTTOM_FLEX, minHeight: 0 }} />

      <DialogFooter orientation="vertical">
        <LoadingButton
          fullWidth
          isLoading={isLoading}
          variant="contained"
          size="large"
          onClick={onContinue}
        >
          {isBecomingRestricted ? "Next" : "Got it"}
        </LoadingButton>
      </DialogFooter>
    </Stack>
  );
}
